import React, { useEffect, useRef, useState } from "react";
import { FileText, Mail, AlertCircle } from "lucide-react";
import { POKUTILS } from "../config/API";

const getFileNameFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "Verification_Letter.docx";
  } catch {
    return "Verification_Letter.docx";
  }
};

const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Fetch failed");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "Verification_Letter.docx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "Verification_Letter.docx";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const VerificationLetter = () => {
  const [paymentType, setPaymentType] = useState(
    () => sessionStorage.getItem("verificationLetterPaymentType") || ""
  );
  const [fileName, setFileName] = useState("");
  const [phase, setPhase] = useState("preparing"); // preparing | success | empty
  const [progress, setProgress] = useState(8);
  const [statusText, setStatusText] = useState("Starting…");
  const hasTriggeredDownload = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let progressTimer = null;

    const animateProgress = (from, to, durationMs) =>
      new Promise((resolve) => {
        const start = performance.now();
        const tick = (now) => {
          if (cancelled) {
            resolve();
            return;
          }
          const t = Math.min(1, (now - start) / durationMs);
          const eased = 1 - (1 - t) * (1 - t);
          setProgress(Math.round(from + (to - from) * eased));
          if (t < 1) {
            progressTimer = requestAnimationFrame(tick);
          } else {
            resolve();
          }
        };
        progressTimer = requestAnimationFrame(tick);
      });

    const run = async () => {
      try {
        setPhase("preparing");
        setProgress(8);
        setStatusText("Connecting…");
        await animateProgress(8, 22, 450);
        if (cancelled) return;

        setStatusText("Looking up your recent payment…");
        const fetchPromise = fetch(POKUTILS, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
          body: new URLSearchParams({
            action: "findrecentpaymenttype",
            saveletter: true,
          }),
        });

        await animateProgress(22, 48, 750);
        const response = await fetchPromise;
        if (cancelled) return;

        if (!response.ok) {
          setPhase("empty");
          return;
        }

        setStatusText("Preparing your verification letter…");
        await animateProgress(48, 74, 650);

        const data = await response.json();
        if (cancelled) return;

        if (data?.status === "success" && data?.paymenttype) {
          setPaymentType(data.paymenttype);
          sessionStorage.setItem("verificationLetterPaymentType", data.paymenttype);

          if (data.documenturl) {
            const name = getFileNameFromUrl(data.documenturl);
            setFileName(name);
            sessionStorage.setItem("verificationLetterDocumentUrl", data.documenturl);

            setStatusText(`Downloading your ${data.paymenttype} letter…`);
            await animateProgress(74, 93, 550);

            if (!hasTriggeredDownload.current) {
              hasTriggeredDownload.current = true;
              await downloadFile(data.documenturl, name);
            }
            if (cancelled) return;

            setStatusText("Finishing up…");
            await animateProgress(93, 100, 400);
            await sleep(250);
            if (!cancelled) setPhase("success");
          } else {
            setPhase("empty");
          }
        } else {
          setPaymentType("");
          sessionStorage.removeItem("verificationLetterPaymentType");
          sessionStorage.removeItem("verificationLetterDocumentUrl");
          setPhase("empty");
        }
      } catch (error) {
        console.error("Failed to fetch verification letter info:", error);
        if (!cancelled) setPhase("empty");
      }
    };

    run();

    return () => {
      cancelled = true;
      if (progressTimer) cancelAnimationFrame(progressTimer);
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Header — same pattern as other portal pages */}
        <div className="relative border-b border-gray-100 bg-gradient-to-r from-primary/5 via-white to-white px-5 py-4 sm:px-6">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-primary" />
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FileText className="h-4 w-4" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 sm:text-xl">
                Verification Letter
              </h1>
              <p className="text-xs text-gray-500 sm:text-sm">
                Payment verification document
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 py-8 sm:px-8 sm:py-10">
          {phase === "preparing" && (
            <div className="mx-auto max-w-md">
              <p className="text-base font-semibold text-gray-900">
                {paymentType
                  ? `Preparing your ${paymentType} letter`
                  : "Preparing your letter"}
              </p>
              <p className="mt-1 text-sm text-gray-500">{statusText}</p>

              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between text-xs font-medium text-gray-500">
                  <span>Progress</span>
                  <span className="tabular-nums text-primary">{progress}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="mt-6 space-y-3 border-t border-gray-100 pt-5">
                {[
                  { label: "Verify payment type", done: progress >= 48 },
                  { label: "Prepare letter", done: progress >= 74 },
                  { label: "Download document", done: progress >= 100 },
                ].map((step) => (
                  <div
                    key={step.label}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className={step.done ? "text-gray-800" : "text-gray-400"}>
                      {step.label}
                    </span>
                    <span
                      className={`text-xs font-medium ${
                        step.done ? "text-primary" : "text-gray-300"
                      }`}
                    >
                      {step.done ? "Done" : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase === "success" && (
            <div className="mx-auto max-w-md">
              <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Download complete
                </p>
                <p className="mt-1 text-base font-semibold text-gray-900 sm:text-lg">
                  You have downloaded your {paymentType} Letter.
                </p>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-gray-600">
                Please contact Enrollment for additional assistance.
              </p>

              {fileName && (
                <div className="mt-5 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-primary shadow-sm">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {fileName}
                    </p>
                    <p className="text-xs text-gray-500">Word document</p>
                  </div>
                </div>
              )}

              <div className="mt-6 flex gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Need help?</p>
                  <p className="mt-0.5 text-sm text-gray-600">
                    Please contact Enrollment for additional assistance.
                  </p>
                </div>
              </div>
            </div>
          )}

          {phase === "empty" && (
            <div className="mx-auto max-w-md text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                <AlertCircle className="h-6 w-6" />
              </div>
              <p className="text-base font-semibold text-gray-900">
                No letter available
              </p>
              <p className="mt-2 text-sm text-gray-600">
                No verification letter is available at this time. Please contact
                Enrollment for additional assistance.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationLetter;
